import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { APIClient } from '../services/APIClient';
import { userAuth } from '../services/firebase-config';

interface Announcement {
  id: number;
  datetime: string | number; // YYYYMMDDHHmm形式（文字列または数値）
  title: string;
  content: string;
  priority: 'normal' | 'high' | 'urgent';
}

export const useAnnouncements = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { i18n } = useTranslation();
  const [apiClient] = useState(() => new APIClient('/api'));

  // Fetch unread announcements
  const fetchUnreadAnnouncements = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const lang = i18n.language === 'ja' ? 'ja' : 'en';
      console.log('[useAnnouncements] Fetching announcements, lang:', lang);
      
      const url = `/api/announcements/unread?lang=${lang}`;
      console.log('[useAnnouncements] Fetching from URL:', url);
      
      const response = await apiClient.authenticatedFetch(url, { method: 'GET' });
      
      const data = await response.json();
      console.log('[useAnnouncements] Response:', data);
      
      if (data.success && data.data) {
        console.log('[useAnnouncements] Setting announcements:', data.data);
        setAnnouncements(data.data);
      } else {
        console.log('[useAnnouncements] No announcements found');
        setAnnouncements([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch announcements:', error);
      setError(error.message || 'Failed to fetch announcements');
      setAnnouncements([]);
    } finally {
      setIsLoading(false);
    }
  }, [i18n.language, apiClient]);

  // Mark announcements as read
  const markAsRead = async (latestId: number) => {
    try {
      const response = await apiClient.authenticatedFetch('/api/announcements/mark-read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ latestId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Clear announcements after successful marking
        setAnnouncements([]);
      }
    } catch (error: any) {
      console.error('Failed to mark as read:', error);
      // Even if marking fails, we clear the modal to avoid annoying the user
      setAnnouncements([]);
    }
  };

  // Check and fetch announcements on mount and auth state change
  useEffect(() => {
    let mounted = true;
    
    // Listen for auth state changes
    const unsubscribe = userAuth.onAuthStateChanged(async (user) => {
      if (!mounted) return;
      
      console.log('[useAnnouncements] Auth state changed - user exists:', !!user);
      
      if (user) {
        try {
          const token = await userAuth.getToken();
          if (token) {
            console.log('[useAnnouncements] Fetching announcements...');
            await fetchUnreadAnnouncements();
          } else {
            setIsLoading(false);
          }
        } catch (error) {
          console.error('[useAnnouncements] Error fetching:', error);
          setIsLoading(false);
        }
      } else {
        console.log('[useAnnouncements] No user logged in');
        setAnnouncements([]);
        setIsLoading(false);
      }
    });
    
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [fetchUnreadAnnouncements]);

  return {
    announcements,
    isLoading,
    error,
    markAsRead,
    refetch: fetchUnreadAnnouncements
  };
};